import React, { useState } from 'react';
import styled from 'styled-components';
import {
  FaChevronDown,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
} from 'react-icons/fa';

const mono = "'JetBrains Mono', 'Consolas', monospace";
const accent = '#00B4D8';
const borderSoft = 'rgba(255, 255, 255, 0.06)';
const borderStrong = 'rgba(255, 255, 255, 0.14)';

/* ---------- container ---------- */

const ReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ReportHeader = styled.header`
  position: relative;
  background:
    linear-gradient(135deg, rgba(0, 180, 216, 0.06), transparent 55%),
    rgba(36, 52, 77, 0.7);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 1rem 1.25rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const ReportTitle = styled.h2`
  margin: 0 0 0.4rem;
  color: var(--text-primary);
  font-size: 1.25rem;
  letter-spacing: -0.005em;
  display: flex;
  align-items: center;
  gap: 0.55rem;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${accent};
    box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.14);
  }
`;

const ReportSubtitle = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.92rem;
`;

/* ---------- expandable section ---------- */

const ExpandableSection = styled.section`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 40%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  overflow: hidden;
`;

const ExpandHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.1rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${({ open }) => (open ? borderSoft : 'transparent')};
  color: var(--text-primary);
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover { background: rgba(255, 255, 255, 0.025); }
`;

const ExpandIndex = styled.span`
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  color: ${accent};
  margin-right: 0.7rem;
`;

const ExpandTitle = styled.h4`
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
`;

const ExpandIcon = styled(FaChevronDown)`
  color: ${accent};
  transition: transform 0.18s ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  font-size: 0.85rem;
`;

const ExpandContent = styled.div`
  padding: 1rem 1.1rem 1.15rem;
`;

/* ---------- executive summary ---------- */

const ExecutiveSummary = styled.div`
  background: rgba(11, 19, 32, 0.55);
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${accent};
  border-radius: 4px;
  padding: 1rem 1.1rem;
  white-space: pre-wrap;
  color: var(--text-secondary);
  line-height: 1.55;
  font-size: 0.92rem;
`;

/* ---------- competitor cards (sub-sección) ---------- */

const CompetitorBlock = styled.article`
  background: rgba(11, 19, 32, 0.5);
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${({ accentColor }) => accentColor || accent};
  border-radius: 4px;
  padding: 1rem 1.1rem;
  margin-bottom: 0.8rem;

  &:last-child { margin-bottom: 0; }
`;

const CompetitorName = styled.h5`
  margin: 0 0 0.6rem;
  color: ${({ color }) => color || 'var(--text-primary)'};
  font-size: 1rem;
`;

const CompetitorDesc = styled.p`
  margin: 0 0 0.85rem;
  color: var(--text-secondary);
  font-size: 0.88rem;
  line-height: 1.5;
`;

const SubBlockTitle = styled.h6`
  margin: 0.85rem 0 0.5rem;
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ color }) => color || 'var(--text-disabled)'};
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const ItemRow = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-secondary);
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 0.2rem;
    font-size: 0.75rem;
    color: ${({ color }) => color || accent};
  }
`;

/* ---------- comparison matrix ---------- */

const MatrixContainer = styled.div`
  overflow-x: auto;
  border: 1px solid ${borderSoft};
  border-radius: 6px;

  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.08); border-radius: 6px; }
`;

const ComparisonMatrix = styled.table`
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
`;

const MatrixHeader = styled.th`
  padding: 0.7rem 0.85rem;
  text-align: left;
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  font-weight: 500;
  background: rgba(255, 255, 255, 0.025);
  border-bottom: 1px solid ${borderStrong};
  white-space: nowrap;
`;

const MatrixCell = styled.td`
  padding: 0.7rem 0.85rem;
  border-bottom: 1px solid ${borderSoft};
  color: var(--text-secondary);
  vertical-align: top;
  font-size: 0.85rem;
  line-height: 1.4;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  li {
    position: relative;
    padding-left: 0.7rem;
    margin-bottom: 0.25rem;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.55em;
      width: 4px;
      height: 1px;
      background: ${accent};
      opacity: 0.7;
    }

    &:last-child { margin-bottom: 0; }
  }
`;

const MatrixRow = styled.tr`
  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
  &:last-child td { border-bottom: none; }
`;

const NamePill = styled.div`
  font-weight: 600;
  color: ${({ color }) => color || 'var(--text-primary)'};
  font-size: 0.88rem;
  display: flex;
  align-items: center;
  gap: 0.45rem;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ color }) => color || accent};
  }
`;

/* ---------- recommendations ---------- */

const RecommendationCard = styled.article`
  position: relative;
  background:
    linear-gradient(180deg, rgba(0, 180, 216, 0.04), transparent 40%),
    rgba(11, 19, 32, 0.55);
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${accent};
  border-radius: 4px;
  padding: 0.95rem 1.1rem;
  margin-bottom: 0.7rem;

  &:last-child { margin-bottom: 0; }
`;

const RecHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 0.45rem;
`;

const RecIndex = styled.span`
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.16em;
  color: ${accent};
  background: rgba(0, 180, 216, 0.1);
  border: 1px solid rgba(0, 180, 216, 0.3);
  border-radius: 3px;
  padding: 2px 6px;
`;

const RecTitle = styled.h5`
  margin: 0;
  color: var(--text-primary);
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  svg { color: ${accent}; font-size: 0.85rem; }
`;

const RecContent = styled.p`
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.55;
  font-size: 0.88rem;
`;

/* ---------- componente ---------- */

const SECTIONS = [
  { id: 'executiveSummary', label: 'Resumen ejecutivo' },
  { id: 'competitorProfiles', label: 'Perfiles de competidores' },
  { id: 'comparativeAnalysis', label: 'Análisis comparativo' },
  { id: 'recommendations', label: 'Recomendaciones estratégicas' },
];

const StrategicAnalysisReport = ({ report }) => {
  const [open, setOpen] = useState({
    executiveSummary: true,
    competitorProfiles: false,
    comparativeAnalysis: false,
    recommendations: false,
  });

  if (!report) return null;

  const toggle = (id) => setOpen((s) => ({ ...s, [id]: !s[id] }));

  return (
    <ReportContainer>
      <ReportHeader>
        <ReportTitle>{report.title}</ReportTitle>
        <ReportSubtitle>{report.subtitle}</ReportSubtitle>
      </ReportHeader>

      {SECTIONS.map((sec, idx) => {
        const isOpen = open[sec.id];
        return (
          <ExpandableSection key={sec.id}>
            <ExpandHeader open={isOpen} onClick={() => toggle(sec.id)} type="button">
              <ExpandTitle>
                <ExpandIndex>{String(idx + 1).padStart(2, '0')}</ExpandIndex>
                {sec.label}
              </ExpandTitle>
              <ExpandIcon $open={isOpen} />
            </ExpandHeader>

            {isOpen && (
              <ExpandContent>
                {sec.id === 'executiveSummary' && (
                  <ExecutiveSummary>{report.executiveSummary}</ExecutiveSummary>
                )}

                {sec.id === 'competitorProfiles' &&
                  report.competitors?.map((c, i) => (
                    <CompetitorBlock key={i} accentColor={c.color}>
                      <CompetitorName color={c.color}>{c.name}</CompetitorName>
                      <CompetitorDesc>{c.description}</CompetitorDesc>

                      <SubBlockTitle color="var(--alert-success)">
                        Fortalezas
                      </SubBlockTitle>
                      <ItemList>
                        {c.strengths.map((s, idx2) => (
                          <ItemRow key={idx2} color="var(--alert-success)">
                            <FaCheckCircle />
                            <span>{s}</span>
                          </ItemRow>
                        ))}
                      </ItemList>

                      <SubBlockTitle color="var(--alert-error)">
                        Debilidades
                      </SubBlockTitle>
                      <ItemList>
                        {c.weaknesses.map((w, idx2) => (
                          <ItemRow key={idx2} color="var(--alert-error)">
                            <FaExclamationTriangle />
                            <span>{w}</span>
                          </ItemRow>
                        ))}
                      </ItemList>
                    </CompetitorBlock>
                  ))}

                {sec.id === 'comparativeAnalysis' && (
                  <MatrixContainer>
                    <ComparisonMatrix>
                      <thead>
                        <tr>
                          <MatrixHeader>Competidor</MatrixHeader>
                          <MatrixHeader>Líderes clave</MatrixHeader>
                          <MatrixHeader>Estrategia</MatrixHeader>
                          <MatrixHeader>Fortalezas</MatrixHeader>
                          <MatrixHeader>Vulnerabilidades</MatrixHeader>
                          <MatrixHeader>Temas principales</MatrixHeader>
                        </tr>
                      </thead>
                      <tbody>
                        {report.competitors?.map((c, i) => (
                          <MatrixRow key={i}>
                            <MatrixCell>
                              <NamePill color={c.color}>{c.name}</NamePill>
                            </MatrixCell>
                            <MatrixCell>{c.leaders}</MatrixCell>
                            <MatrixCell>{c.strategy}</MatrixCell>
                            <MatrixCell>
                              <ul>
                                {c.strengths.slice(0, 3).map((s, idx2) => (
                                  <li key={idx2}>{s}</li>
                                ))}
                              </ul>
                            </MatrixCell>
                            <MatrixCell>
                              <ul>
                                {c.weaknesses.slice(0, 3).map((w, idx2) => (
                                  <li key={idx2}>{w}</li>
                                ))}
                              </ul>
                            </MatrixCell>
                            <MatrixCell>{c.mainTopics?.join(', ')}</MatrixCell>
                          </MatrixRow>
                        ))}
                      </tbody>
                    </ComparisonMatrix>
                  </MatrixContainer>
                )}

                {sec.id === 'recommendations' &&
                  report.recommendations?.map((r, i) => (
                    <RecommendationCard key={i}>
                      <RecHeader>
                        <RecIndex>R-{String(i + 1).padStart(2, '0')}</RecIndex>
                        <RecTitle>
                          <FaInfoCircle />
                          {r.title}
                        </RecTitle>
                      </RecHeader>
                      <RecContent>{r.content}</RecContent>
                    </RecommendationCard>
                  ))}
              </ExpandContent>
            )}
          </ExpandableSection>
        );
      })}
    </ReportContainer>
  );
};

export default StrategicAnalysisReport;
